import { Trans } from '@lingui/react/macro';
import { brands } from 'noddi-types';
import { NoddiButton, NoddiLanguageSelector, NoddiLink, useIsMobile } from 'noddi-ui-web';
import { customerRoutes } from 'noddi-util';
import { tracking, useAuthContext, useLanguageContext } from 'noddi-web';
import { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useNewBookingPath } from '../../hooks/useNewBookingPath';
import { useClearBookingDrafts } from '../../hooks/useStartNewBooking';
import { useCurrentBrand } from '../../stores/BrandStore/brandStore';
import { getSupportedLocales } from '../../utils/translation';
import { BrandLogo } from '../Brand/BrandLogo';
import { CustomerLoginModal } from '../Login/CustomerLoginModal';
import { LogoLink } from './LogoLink';
import { NavWrapper } from './NavWrapper';
import { TronderdekkHeaderNav } from './TronderdekkHeaderNav';
import { usePageWidth } from './usePageWidth';

export const CustomerHeaderNav = () => {
  const { currentBrand } = useCurrentBrand();

  if (currentBrand === brands.tronderdekk) {
    return <TronderdekkHeaderNav />;
  }

  return <DefaultCustomerHeaderNav />;
};

const DefaultCustomerHeaderNav = () => {
  const { isLoggedIn } = useAuthContext();
  const [openLoginModal, setOpenLoginModal] = useState(false);
  const { pathname } = useLocation();
  const { currentLanguage, setCurrentLanguage } = useLanguageContext();
  const clearBookingDrafts = useClearBookingDrafts();
  const { path: newBookingPath } = useNewBookingPath();
  const width = usePageWidth();

  const isMobileDevice = useIsMobile();

  const isLoginPage = pathname === customerRoutes.login.getPath();
  const isSummaryStep =
    pathname.includes(customerRoutes.newBookingSummary.getBasePath()) ||
    pathname.includes(customerRoutes.bookingProposalConfirmation.getBasePath());
  // use hardcoded string as it has unique slugs
  const isMemberShipsPage = pathname.includes('memberships');
  const isInBookingFlow =
    pathname.startsWith('/bookings/new') ||
    pathname.startsWith('/bookings/service-center') ||
    pathname.startsWith('/p/');

  const onGoToNewBooking = () => {
    clearBookingDrafts();
  };

  const showRightCluster = !isSummaryStep && !isMemberShipsPage;
  // Hide the Book CTA on mobile when Sign in / My profile is also shown — not enough room for both.
  const hasRoomForBookButton = !isMobileDevice || isLoginPage;
  const showBookButton = !isInBookingFlow && hasRoomForBookButton;

  return (
    <NavWrapper width={width}>
      <div className='flex w-full items-center justify-between gap-5'>
        <div className='flex items-center gap-2'>
          <LogoLink>
            <BrandLogo />
          </LogoLink>

          {showRightCluster && (
            <NoddiLanguageSelector
              collapseOnMobile
              className='bg-transparent shadow-none'
              supportedLanguages={getSupportedLocales()}
              currentLanguage={currentLanguage}
              setCurrentLanguage={(locale) => {
                tracking.track('languageChanged', { language: locale });
                setCurrentLanguage(locale);
              }}
            />
          )}
        </div>

        {showRightCluster && (
          <div className='flex items-center gap-2'>
            {!isLoginPage && !isLoggedIn && (
              <NoddiButton variant='ghost' onClick={() => setOpenLoginModal(true)}>
                <Trans>Sign in</Trans>
              </NoddiButton>
            )}

            {!isLoginPage && isLoggedIn && (
              <NoddiLink to={customerRoutes.homepage.getPath()} variant='ghost' endIcon='User'>
                <Trans>My profile</Trans>
              </NoddiLink>
            )}

            {showBookButton && (
              <NoddiLink
                to={newBookingPath}
                onClick={onGoToNewBooking}
                endIcon='Calendar'
                variant='primary'
                size={isMobileDevice ? 'small' : 'default'}
              >
                <Trans>Book</Trans>
              </NoddiLink>
            )}
          </div>
        )}
      </div>
      <CustomerLoginModal onClose={() => setOpenLoginModal(false)} open={openLoginModal} />
    </NavWrapper>
  );
};
